// src/components/AmenityPublicDashboard.jsx
import React, { useState, useEffect } from "react";
import styles from "../styles/PublicDashboard.module.css";
import axios from "axios";
import { MapContainer, TileLayer, Marker, Popup, Tooltip, GeoJSON } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { FaTimes, FaFileAlt, FaUser, FaMapMarkerAlt, FaCalendarAlt, FaHome } from "react-icons/fa";
// Configure Leaflet default marker icons
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';
const API = process.env.REACT_APP_OFFICER_API_URL;

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

// Custom marker icons for different project statuses (kept for any remaining markers)
const getMarkerIcon = (status) => {
  const iconColors = {
    "Completed": "#2e844a",
    "Ongoing": "#ff8c00",
    "Not Started": "#6b6b6b",
    "Delayed": "#c23934"
  };

  const color = iconColors[status] || "#0070d2";

  return L.divIcon({
    className: 'custom-marker',
    html: `<div style="background-color: ${color}; width: 14px; height: 14px; border-radius: 50%; border: 2px solid white; box-shadow: 0 2px 4px rgba(0,0,0,0.3);"></div>`,
    iconSize: [18, 18],
    popupAnchor: [0, -9]
  });
};

// Function to get polygon style based on project type and status - LIGHT AND TRANSPARENT
const getPolygonStyle = (feature) => {
  const projectType = feature?.properties?.projectType;
  const status = feature?.properties?.status;

  // Base colors by project type - light, transparent colors
  const typeColors = {
    "Road": "#3498db",
    "Gutter": "#e67e22",
    "Footpath": "#2ecc71",
    "Drainage": "#9b59b6",
    "Water Supply": "#1abc9c",
    "Other": "#95a5a6"
  };

  // Status-based opacity and weight - VERY LIGHT FILL OPACITY
  const statusStyles = {
    "Completed": { opacity: 0.6, weight: 2, fillOpacity: 0.27, dashArray: null },
    "Ongoing": { opacity: 0.7, weight: 2.5, fillOpacity: 0.27, dashArray: null },
    "Not Started": { opacity: 0.5, weight: 1.5, fillOpacity: 0.27, dashArray: "5, 5" },
    "Delayed": { opacity: 0.7, weight: 2.5, fillOpacity: 0.27, dashArray: "10, 5" }
  };

  const baseColor = typeColors[projectType] || "#3498db";
  const statusStyle = statusStyles[status] || statusStyles["Not Started"];

  return {
    color: baseColor,
    weight: statusStyle.weight,
    opacity: statusStyle.opacity,
    fillColor: baseColor,
    fillOpacity: statusStyle.fillOpacity,
    dashArray: statusStyle.dashArray,
    // Add slight glow effect for better visibility without obscuring map
    className: 'light-polygon'
  };
};

// Helper function to calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lon1, lat2, lon2) => {
  const R = 6371; // Earth's radius in km
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
    Math.sin(dLon / 2) * Math.sin(dLon / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
};

// Helper function to convert LineString to Polygon (creates a buffer/width around the line)
const lineStringToPolygon = (coordinates, widthInMeters = 15) => {
  // Convert meters to degrees (approximate - 1 degree ≈ 111,000 meters)
  const widthInDegrees = widthInMeters / 111000;

  const polygons = [];

  for (let i = 0; i < coordinates.length - 1; i++) {
    const p1 = coordinates[i];
    const p2 = coordinates[i + 1];

    // Calculate perpendicular direction
    const dx = p2[0] - p1[0];
    const dy = p2[1] - p1[1];
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length === 0) continue;

    // Normalized perpendicular vector (rotate 90 degrees)
    const perpX = -dy / length;
    const perpY = dx / length;

    // Scale by width
    const offsetX = perpX * widthInDegrees;
    const offsetY = perpY * widthInDegrees;

    // Create rectangle for this segment
    const rect = [
      [p1[0] + offsetX, p1[1] + offsetY],
      [p1[0] - offsetX, p1[1] - offsetY],
      [p2[0] - offsetX, p2[1] - offsetY],
      [p2[0] + offsetX, p2[1] + offsetY],
      [p1[0] + offsetX, p1[1] + offsetY] // Close the polygon
    ];

    polygons.push(rect);
  }

  // Return the first polygon (for simplicity, in production you'd merge overlapping polygons)
  return polygons.length > 0 ? polygons[0] : null;
};

const formatStatus = (status) => {
  if (!status) return "Not Started";

  const s = status.toLowerCase();

  if (s === "completed") return "Completed";
  if (s === "ongoing") return "Ongoing";
  if (s === "not_started") return "Not Started";
  if (s === "delayed") return "Delayed";

  return "Not Started";
};

const mapApiToBlock = (raw, index) => {
  const d = raw.data || {};

  return {
    index: raw.index ?? index + 1,
    timestamp: raw.timestamp ? new Date(raw.timestamp) : new Date(),
    hash: raw.currentHash || "",
    previousHash: raw.previousHash || "",
    nonce: raw.nonce || 0,
    merkleRoot: raw.dataHash || "",

    transactions: [
      {
        transactionId: d.workId || "",
        status: formatStatus(d.workStatus),
        stageName: "",
        timestamp: raw.timestamp ? new Date(raw.timestamp) : new Date(),

        sender: {
          name: d.officerName || "",
          role: ""
        },

        validators: d.validators || [],

        formdata: {
          "Project ID": d.workId || "",
          "Project Name": d.projectName || "",
          "Project Type": d.projectType || "",
          "Ward": d.wardNumber || "",
          "Officer Name": d.officerName || "",
          "Contractor Name": d.contractorName || "",
          "Estimated Cost":
            d.estimatedCost
              ? `₹${Number(d.estimatedCost).toLocaleString()}`
              : "",

          // 🔥 ADD THESE (YOU MISSED)
          "Full Address": d.fullAddress || "",
          "Project Description": d.description || "",

          // If available in future
          "State": d.state || "",
          "District": d.district || "",
          "City": d.city || "",

          "Latitude": d.latitude || "",
          "Longitude": d.longitude || "",
          "GeoJSON": d.geojson || "",

          "Project Start Date": d.projectStartDate || "",
          "Expected Completion Date": d.expectedCompletionDate || "",
          "Actual Completion Date": d.actualCompletionDate || "",
          "Payment Status": d.paymentStatus || "",
          "Payment History": d.paymentHistory || "",
          "Delay Reason": d.delayReason || "",

          "Work Status": formatStatus(d.workStatus)
        }
      }
    ]
  };
};

export default function AmenityPublicDashboard() {
  const [blocks, setBlocks] = useState([]);
  const [search, setSearch] = useState("");
  const [view, setView] = useState("blocks");
  const [selected, setSelected] = useState(null);
  const [statusFilter, setStatusFilter] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [latitude, setLatitude] = useState("");
  const [longitude, setLongitude] = useState("");
  const [searchRadius, setSearchRadius] = useState(5);
  const [showModal, setShowModal] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    location: true,
    admin: false,
    financial: false,
    timeline: false,
    technical: false,
    documents: false
  });

  // Project Status
  const statusList = ["Not Started", "Ongoing", "Completed", "Delayed"];

  // Project Types
  const typeList = ["Road", "Gutter", "Footpath", "Drainage", "Water Supply", "Other"];

  // Toggle section expansion in modal
  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  // Sample Data with POLYGON coordinates (covering actual road/infrastructure areas)
  useEffect(() => {
    fetchBlockchain();
  }, []);

  const fetchBlockchain = async () => {
    try {
      const res = await axios.get(
        `${API}/api/application/blockchain`
      );

      const mapped = (res.data.data || []).map((item, index) =>
        mapApiToBlock(item, index)
      );

      setBlocks(mapped);
    } catch (err) {
      console.error("API Error:", err);
    }
  };

  // Coordinate-based filtering logic
  const filtered = blocks.filter((b) => {
    const tx = b.transactions?.[0];
    const data = tx?.formdata || {};

    const query = search.toLowerCase();

    const matchesSearch =
      (data["Project ID"] || "").toLowerCase().includes(query) ||
      (data["Project Name"] || "").toLowerCase().includes(query) ||
      (data["Officer Name"] || "").toLowerCase().includes(query) ||
      (data["Contractor Name"] || "").toLowerCase().includes(query) ||
      (data["Ward"] || "").toLowerCase().includes(query) ||
      (b.hash || "").toLowerCase().includes(query);

    const matchesStatus =
      !statusFilter || data["Work Status"] === statusFilter;

    const matchesType =
      !typeFilter || data["Project Type"] === typeFilter;

    // Coordinate-based filtering
    let matchesCoordinates = true;
    if (latitude && longitude) {
      const projectLat = parseFloat(data["Latitude"]);
      const projectLng = parseFloat(data["Longitude"]);
      if (!isNaN(projectLat) && !isNaN(projectLng)) {
        const distance = calculateDistance(
          parseFloat(latitude),
          parseFloat(longitude),
          projectLat,
          projectLng
        );
        matchesCoordinates = distance <= searchRadius;
      } else {
        matchesCoordinates = false;
      }
    }

    return matchesSearch && matchesStatus && matchesType && matchesCoordinates;
  });

  // Handle row/block click to open modal
  const handleItemClick = (block) => {
    setSelected(block);
    setShowModal(true);
  };

  // Close modal
  const closeModal = () => {
    setShowModal(false);
    setSelected(null);
    setExpandedSections({
      basic: true,
      location: true,
      admin: false,
      financial: false,
      timeline: false,
      technical: false,
      documents: false
    });
  };

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setLatitude(position.coords.latitude.toString());
          setLongitude(position.coords.longitude.toString());
        },
        (error) => {
          console.error("Error getting location:", error);
          alert("Unable to get your location. Please enter coordinates manually.");
        }
      );
    } else {
      alert("Geolocation is not supported by your browser.");
    }
  };

  // Helper to render status badge
  const getStatusBadgeClass = (status) => {
    switch (status) {
      case "Completed": return styles.statusCompleted;
      case "Ongoing": return styles.statusOngoing;
      case "Not Started": return styles.statusNotStarted;
      case "Delayed": return styles.statusDelayed;
      default: return styles.statusDefault;
    }
  };

  // Helper to render payment status badge
  const getPaymentStatusClass = (status) => {
    switch (status?.toLowerCase()) {
      case "completed": return styles.paymentCompleted;
      case "partial": return styles.paymentPartial;
      case "pending": return styles.paymentPending;
      default: return styles.statusDefault;
    }
  };

  const display = (val) => {
    return val && val !== "" ? val : "—";
  };

  const showField = (val) => {
    return val && val !== "" && val !== "—";
  };

  // Center of Sangli city
  const sangliCenter = [16.8524, 74.5815];

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <h2 className={styles.title}>Infrastructure Blockchain Dashboard</h2>
      </div>

      {/* Controls */}
      <div className={styles.controls}>
        <input
          placeholder="Search by Project ID, Name, Officer, Contractor, or Ward..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className={styles.searchInput}
        />

        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} className={styles.filterSelect}>
          <option value="">All Status</option>
          {statusList.map((s) => (
            <option key={s}>{s}</option>
          ))}
        </select>

        <select value={typeFilter} onChange={(e) => setTypeFilter(e.target.value)} className={styles.filterSelect}>
          <option value="">All Types</option>
          {typeList.map((t) => (
            <option key={t}>{t}</option>
          ))}
        </select>

        <select value={view} onChange={(e) => setView(e.target.value)} className={styles.viewSelect}>
          <option value="table">Table View</option>
          <option value="blocks">Block View</option>
          <option value="map">Map View</option>
        </select>
      </div>

      {/* Coordinate Search Section */}
      <div className={styles.coordinateSection}>
        <div className={styles.coordinateHeader}>
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z" stroke="currentColor" strokeWidth="2" />
            <circle cx="12" cy="9" r="2.5" stroke="currentColor" strokeWidth="2" />
          </svg>
          <span>Search by Coordinates</span>
        </div>
        <div className={styles.coordinateInputs}>
          <div className={styles.coordInputGroup}>
            <label>Latitude</label>
            <input
              type="text"
              placeholder="e.g., 16.8524"
              value={latitude}
              onChange={(e) => setLatitude(e.target.value)}
              className={styles.coordInput}
            />
          </div>
          <div className={styles.coordInputGroup}>
            <label>Longitude</label>
            <input
              type="text"
              placeholder="e.g., 74.5815"
              value={longitude}
              onChange={(e) => setLongitude(e.target.value)}
              className={styles.coordInput}
            />
          </div>
          <div className={styles.coordInputGroup}>
            <label>Radius (km)</label>
            <input
              type="number"
              placeholder="5"
              value={searchRadius}
              onChange={(e) => setSearchRadius(parseFloat(e.target.value) || 5)}
              className={styles.radiusInput}
              min="0.5"
              max="50"
              step="0.5"
            />
          </div>
          <button onClick={getCurrentLocation} className={styles.locationButton}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M12 2v4M12 18v4M4 12H2M6 12H4M20 12h-2M22 12h-2M19.07 4.93l-2.83 2.83M7.76 16.24l-2.83 2.83M16.24 16.24l2.83 2.83M4.93 19.07l2.83-2.83" stroke="currentColor" strokeWidth="2" />
              <circle cx="12" cy="12" r="4" stroke="currentColor" strokeWidth="2" />
            </svg>
            Use My Location
          </button>
          {(latitude || longitude) && (
            <button onClick={() => { setLatitude(""); setLongitude(""); }} className={styles.clearCoordButton}>
              Clear
            </button>
          )}
        </div>
        {latitude && longitude && (
          <div className={styles.coordinateResults}>
            Found {filtered.length} project(s) within {searchRadius} km of selected location
          </div>
        )}
      </div>

      {/* Table View */}
      {view === "table" ? (
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>Block #</th>
                <th>Project ID</th>
                <th>Project Name</th>
                <th>Type</th>
                <th>Status</th>
                <th>Ward</th>
                <th>Officer</th>
                <th>Contractor</th>
                <th>Timestamp</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((b) => {
                const data = b.transactions?.[0]?.formdata || {};
                return (
                  <tr key={b.hash} onClick={() => handleItemClick(b)} className={styles.tableRow}>
                    <td className={styles.blockNumber}>{b.index}</td>
                    <td className={styles.projectId}>
                      {data["Project ID"] || "—"}
                    </td>
                    <td className={styles.projectName}>{data["Project Name"] || "—"}</td>
                    <td><span className={styles.typeBadge}>{data["Project Type"] || "—"}</span></td>
                    <td><span className={`${styles.statusBadge} ${getStatusBadgeClass(data["Work Status"])}`}>{data["Work Status"]}</span></td>
                    <td>{data["Ward"] || "—"}</td>
                    <td className={styles.officerName}>{data["Officer Name"] || "—"}</td>
                    <td className={styles.contractorName}>
                      {data["Contractor Name"] || "—"}
                    </td>
                    <td>{new Date(b.timestamp).toLocaleString()}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : view === "blocks" ? (
        <div className={styles.blockView}>
          <div className={styles.chainWrapper}>
            {filtered.map((b, i) => {
              const data = b.transactions?.[0]?.formdata || {};
              return (
                <div key={b.hash} className={styles.chainItem}>
                  <div className={styles.block} onClick={() => handleItemClick(b)}>
                    <div className={styles.blockHeader}>
                      <span className={styles.blockNumber}>Block #{b.index}</span>
                      <span className={`${styles.blockStatus} ${getStatusBadgeClass(data["Work Status"])}`}>
                        {data["Work Status"]}
                      </span>
                    </div>
                    <div className={styles.blockBody}>
                      <p><strong>Project:</strong> {data["Project Name"] || "—"}</p>
                      <p><strong>ID:</strong> {data["Project ID"]}</p>
                      <p><strong>Type:</strong> {data["Project Type"] || "—"}</p>
                      <p><strong>Ward:</strong> {data["Ward"] || "—"}</p>
                      <p><strong>Officer:</strong> {data["Officer Name"] || "—"}</p>
                      <p><strong>Cost:</strong> {data["Estimated Cost"] || "—"}</p>
                    </div>
                  </div>
                  {i < filtered.length - 1 && <div className={styles.arrow}>→</div>}
                </div>
              );
            })}
          </div>
        </div>
      ) : (
        /* Map View with GeoJSON Polygons */
        <div className={styles.mapView}>
          <MapContainer
            center={latitude && longitude ? [parseFloat(latitude), parseFloat(longitude)] : sangliCenter}
            zoom={latitude && longitude ? 14 : 13}
            style={{ height: "500px", width: "100%", borderRadius: "12px" }}
            className={styles.mapContainer}
          >
            <TileLayer
              url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
              attribution='Tiles &copy; Esri &mdash; Source: Esri, i-cubed, USDA, USGS, AEX, GeoEye, Getmapping, Aerogrid, IGN, IGP, UPR-EGP, and the GIS User Community'
            />

            {/* Render GeoJSON polygons for each project */}
            {filtered.map((b) => {
              const data = b.transactions?.[0]?.formdata || {};
              const geoJsonStr = data["GeoJSON"];

              if (!geoJsonStr) return null;

              try {
                const geoJsonData = JSON.parse(geoJsonStr);

                // Create a feature with properties
                const feature = {
                  type: "Feature",
                  geometry: geoJsonData,
                  properties: {
                    projectName: data["Project Name"],
                    projectId: data["Project ID"],
                    projectType: data["Project Type"],
                    status: data["Work Status"],
                    ward: data["Ward"],
                    officerName: data["Officer Name"],
                    contractorName: data["Contractor Name"],
                    estimatedCost: data["Estimated Cost"],
                    description: data["Project Description"],
                    nearbyLandmarks: data["Nearby Landmarks"],
                    blockData: b // Store block data for click handler
                  }
                };

                return (
                  <GeoJSON
                    key={b.hash}
                    data={feature}
                    style={getPolygonStyle}
                    eventHandlers={{
                      click: (e) => {
                        // Trigger the same modal as clicking on markers
                        handleItemClick(feature.properties.blockData);
                      }
                    }}
                  />
                );
              } catch (error) {
                console.error("Error parsing GeoJSON:", error);
                return null;
              }
            })}

            {/* Show search location marker if coordinates are provided */}
            {latitude && longitude && (
              <Marker
                position={[parseFloat(latitude), parseFloat(longitude)]}
                icon={L.divIcon({
                  className: 'search-marker',
                  html: `<div style="background-color: #ff4444; width: 12px; height: 12px; border-radius: 50%; border: 3px solid white; box-shadow: 0 0 0 2px #ff4444;"></div>`,
                  iconSize: [18, 18]
                })}
              >
                <Popup>
                  <div>Your search location</div>
                </Popup>
              </Marker>
            )}
          </MapContainer>

          {/* Map Legend */}
          <div className={styles.mapLegend}>
            <h4>Project Type Legend</h4>
            <div className={styles.legendItems}>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: "#3498db" }}></div>
                <span>Road</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: "#e67e22" }}></div>
                <span>Gutter</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: "#2ecc71" }}></div>
                <span>Footpath</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: "#9b59b6" }}></div>
                <span>Drainage</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendColor} style={{ backgroundColor: "#1abc9c" }}></div>
                <span>Water Supply</span>
              </div>
            </div>

            <h4 style={{ marginTop: "10px" }}>Status Indicator</h4>
            <div className={styles.legendItems}>
              <div className={styles.legendItem}>
                <div className={styles.legendLine} style={{ backgroundColor: "#2e844a", width: "20px", height: "3px" }}></div>
                <span>Completed (Solid)</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendLine} style={{ backgroundColor: "#ff8c00", width: "20px", height: "4px" }}></div>
                <span>Ongoing (Bold)</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendLine} style={{ backgroundColor: "#6b6b6b", width: "20px", height: "2px", borderTop: "2px dashed #6b6b6b" }}></div>
                <span>Not Started (Dashed)</span>
              </div>
              <div className={styles.legendItem}>
                <div className={styles.legendLine} style={{ backgroundColor: "#c23934", width: "20px", height: "4px", borderTop: "2px dashed #c23934" }}></div>
                <span>Delayed (Dashed Bold)</span>
              </div>
            </div>

            {latitude && longitude && (
              <>
                <h4 style={{ marginTop: "10px" }}>Search Location</h4>
                <div className={styles.legendItems}>
                  <div className={styles.legendItem}>
                    <div className={styles.legendColor} style={{ backgroundColor: "#ff4444" }}></div>
                    <span>Your Search Location</span>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Popup - Redesigned to match reference UI/UX */}
      {showModal && selected && (
        <div className={styles.modalOverlay} onClick={closeModal}>
          <div className={styles.modalContainer} onClick={(e) => e.stopPropagation()}>
            <div className={styles.modalHeader}>
              <h2 className={styles.modalTitle}>
                <FaFileAlt className={styles.modalIcon} />
                {selected.transactions?.[0]?.formdata["Project Name"] || "—"}
              </h2>
              <button
                className={styles.modalCloseButton}
                onClick={closeModal}
                aria-label="Close modal"
              >
                <FaTimes />
              </button>
            </div>

            <div className={styles.modalContent}>
              {/* Left Column */}
              <div className={styles.modalColumn}>
                {/* Basic Project Details Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <FaUser className={styles.sectionIcon} />
                    Basic Project Details
                  </h3>
                  <div className={styles.detailField}>
                    <label>Project ID</label>
                    <div className={styles.detailValue}>
                      {selected.transactions?.[0]?.formdata["Project ID"] || "—"}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Project Name</label>
                    <div className={styles.detailValue}>
                      {selected.transactions?.[0]?.formdata["Project Name"] || "—"}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Project Type</label>
                    <div className={styles.detailValue}>
                      <span className={styles.typeBadge}>{selected.transactions?.[0]?.formdata["Project Type"]}</span>
                    </div>
                  </div>
                  {/*  <div className={styles.detailField}>
                    <label>Department Name</label>
                    <div className={styles.detailValue}>
                      {selected.transactions?.[0]?.formdata["Department Name"]}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Ward Number</label>
                    <div className={styles.detailValue}>
                      {selected.transactions?.[0]?.formdata["Ward Number"]}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Zone/Area Name</label>
                    <div className={styles.detailValue}>
                      {selected.transactions?.[0]?.formdata["Zone/Area Name"]}
                    </div>
                  </div>
                  */}
                  <div className={styles.detailField}>
                    <label>Project Description</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Project Description"])}
                    </div>
                  </div>
                </div>

                {/* Administrative Details Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <FaUser className={styles.sectionIcon} />
                    Administrative Details
                  </h3>
                  <div className={styles.detailField}>
                    <label>Authority Name</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Authority Name"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Officer Name</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Officer Name"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Officer ID</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Officer ID"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Contractor Name</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Contractor Name"])}
                    </div>
                  </div>
                  {showField(selected.transactions?.[0]?.formdata["Contractor ID/License"]) && (
                    <div className={styles.detailField}>
                      <label>Contractor ID/License</label>
                      <div className={styles.detailValue}>
                        {display(selected.transactions?.[0]?.formdata["Contractor ID/License"])}
                      </div>
                    </div>
                  )}
                  {showField(selected.transactions?.[0]?.formdata["Tender ID"]) && (
                    <div className={styles.detailField}>
                      <label>Tender ID</label>
                      <div className={styles.detailValue}>
                        {display(selected.transactions?.[0]?.formdata["Tender ID"])}
                      </div>
                    </div>
                  )}
                  {showField(selected.transactions?.[0]?.formdata["Work Order Number"]) && (
                    <div className={styles.detailField}>
                      <label>Work Order Number</label>
                      <div className={styles.detailValue}>
                        {display(selected.transactions?.[0]?.formdata["Work Order Number"])}
                      </div>
                    </div>
                  )}
                </div>

                {/* Documents Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <FaFileAlt className={styles.sectionIcon} />
                    Documents & Media
                  </h3>
                  <div className={styles.detailField}>
                    <label>DPR Document</label>
                    <div className={styles.detailValue}>
                      <a className={styles.fileLink}>📄 {selected.transactions?.[0]?.formdata["DPR Document"]}</a>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Tender Document</label>
                    <div className={styles.detailValue}>
                      <a className={styles.fileLink}>📄 {selected.transactions?.[0]?.formdata["Tender Document"]}</a>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Work Order Document</label>
                    <div className={styles.detailValue}>
                      <a className={styles.fileLink}>📄 {selected.transactions?.[0]?.formdata["Work Order Document"]}</a>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Approval Documents</label>
                    <div className={styles.detailValue}>
                      <a className={styles.fileLink}>📄 {selected.transactions?.[0]?.formdata["Approval Documents"]}</a>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Site Images (Before)</label>
                    <div className={styles.detailValue}>
                      🖼️ {selected.transactions?.[0]?.formdata["Site Images (Before)"]}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Site Images (During)</label>
                    <div className={styles.detailValue}>
                      🖼️ {selected.transactions?.[0]?.formdata["Site Images (During)"]}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Site Images (After)</label>
                    <div className={styles.detailValue}>
                      🖼️ {selected.transactions?.[0]?.formdata["Site Images (After)"] || "Not available"}
                    </div>
                  </div>
                  {selected.transactions?.[0]?.formdata["Video Proof"] !== "--" && (
                    <div className={styles.detailField}>
                      <label>Video Proof</label>
                      <div className={styles.detailValue}>
                        🎥 {selected.transactions?.[0]?.formdata["Video Proof"]}
                      </div>
                    </div>
                  )}
                </div>

                {/* Blockchain Metadata Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <FaFileAlt className={styles.sectionIcon} />
                    Blockchain Metadata
                  </h3>
                  <div className={styles.detailField}>
                    <label>Block Hash</label>
                    <div className={styles.detailValue}>
                      <code>{selected.hash}</code>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Previous Hash</label>
                    <div className={styles.detailValue}>
                      <code>{selected.previousHash}</code>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Merkle Root</label>
                    <div className={styles.detailValue}>
                      <code>{selected.merkleRoot}</code>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Nonce</label>
                    <div className={styles.detailValue}>
                      {selected.nonce}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Timestamp</label>
                    <div className={styles.detailValue}>
                      {new Date(selected.timestamp).toLocaleString()}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Transaction ID</label>
                    <div className={styles.detailValue}>
                      <code>{selected.transactions?.[0]?.transactionId}</code>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Sender</label>
                    <div className={styles.detailValue}>
                      {selected.transactions?.[0]?.sender?.name} ({selected.transactions?.[0]?.sender?.role})
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Validators</label>
                    <div className={styles.detailValue}>
                      {selected.transactions?.[0]?.validators?.map(v => v.validator).join(", ") || "—"}
                    </div>
                  </div>
                </div>
              </div>

              {/* Right Column */}
              <div className={styles.modalColumn}>
                {/* Location Details Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <FaMapMarkerAlt className={styles.sectionIcon} />
                    Location Details
                  </h3>
                  <div className={styles.detailField}>
                    <label>State</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["State"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>District</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["District"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>City</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["City"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Ward</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Ward"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Full Address</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Full Address"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Latitude</label>
                    <div className={styles.detailValue}>
                      <code>{display(selected.transactions?.[0]?.formdata["Latitude"])}</code>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Longitude</label>
                    <div className={styles.detailValue}>
                      <code>{display(selected.transactions?.[0]?.formdata["Longitude"])}</code>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Nearby Landmarks</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Nearby Landmarks"])}
                    </div>
                  </div>
                </div>

                {/* Financial & Timeline Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <FaCalendarAlt className={styles.sectionIcon} />
                    Financial & Timeline Details
                  </h3>
                  <div className={styles.detailField}>
                    <label>Estimated Cost</label>
                    <div className={styles.detailValue}>
                      <span className={styles.costValue}>{selected.transactions?.[0]?.formdata["Estimated Cost"]}</span>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Approved Budget</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Approved Budget"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Tender Amount</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Tender Amount"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Final Project Cost</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Final Project Cost"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Fund Source</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Fund Source"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Payment Status</label>
                    <div className={styles.detailValue}>
                      <span className={`${getPaymentStatusClass(selected.transactions?.[0]?.formdata["Payment Status"])}`}>
                        {display(selected.transactions?.[0]?.formdata["Payment Status"])}
                      </span>
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Payment History</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Payment History"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Project Start Date</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Project Start Date"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Expected Completion Date</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Expected Completion Date"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Actual Completion Date</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Actual Completion Date"])}
                    </div>
                  </div>
                  <div className={styles.detailField}>
                    <label>Work Status</label>
                    <div className={`${styles.detailValue} ${getStatusBadgeClass(selected.transactions?.[0]?.formdata["Work Status"])}`}>
                      {selected.transactions?.[0]?.formdata["Work Status"]}
                    </div>
                  </div>
                  {showField(selected.transactions?.[0]?.formdata["Delay Reason"]) && (
                    <div className={styles.detailField}>
                      <label>Delay Reason</label>
                      <div className={styles.detailValue}>
                        {selected.transactions?.[0]?.formdata["Delay Reason"]}
                      </div>
                    </div>
                  )}
                </div>

                {/* Technical Details Section */}
                <div className={styles.section}>
                  <h3 className={styles.sectionTitle}>
                    <FaHome className={styles.sectionIcon} />
                    Technical Details
                  </h3>
                  {showField(selected.transactions?.[0]?.formdata["Road Length (meters)"]) && (
                    <>
                      <div className={styles.detailField}>
                        <label>Road Length (meters)</label>
                        <div className={styles.detailValue}>
                          {selected.transactions?.[0]?.formdata["Road Length (meters)"]}
                        </div>
                      </div>
                      <div className={styles.detailField}>
                        <label>Road Width (meters)</label>
                        <div className={styles.detailValue}>
                          {selected.transactions?.[0]?.formdata["Road Width (meters)"]}
                        </div>
                      </div>
                    </>
                  )}
                  <div className={styles.detailField}>
                    <label>Material Used</label>
                    <div className={styles.detailValue}>
                      {display(selected.transactions?.[0]?.formdata["Material Used"])}
                    </div>
                  </div>
                  {showField(selected.transactions?.[0]?.formdata["Thickness (mm)"]) && (
                    <div className={styles.detailField}>
                      <label>Thickness (mm)</label>
                      <div className={styles.detailValue}>
                        {display(selected.transactions?.[0]?.formdata["Thickness (mm)"])}
                      </div>
                    </div>
                  )}
                  {showField(selected.transactions?.[0]?.formdata["Gutter Type/Size"]) && (
                    <div className={styles.detailField}>
                      <label>Gutter Type/Size</label>
                      <div className={styles.detailValue}>
                        {selected.transactions?.[0]?.formdata["Gutter Type/Size"]}
                      </div>
                    </div>
                  )}
                  {showField(selected.transactions?.[0]?.formdata["Footpath Type"]) && (
                    <div className={styles.detailField}>
                      <label>Footpath Type</label>
                      <div className={styles.detailValue}>
                        {selected.transactions?.[0]?.formdata["Footpath Type"]}
                      </div>
                    </div>
                  )}
                  {showField(selected.transactions?.[0]?.formdata["Drainage Type"]) && (
                    <div className={styles.detailField}>
                      <label>Drainage Type</label>
                      <div className={styles.detailValue}>
                        {selected.transactions?.[0]?.formdata["Drainage Type"]}
                      </div>
                    </div>
                  )}
                  <div className={styles.detailField}>
                    <label>Design Specifications</label>
                    <div className={styles.detailValue}>
                      <span className={styles.documentLink}>📄 {display(selected.transactions?.[0]?.formdata["Design Specifications"])}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className={styles.modalActions}>
              <button
                type="button"
                className={styles.closeButton}
                onClick={closeModal}
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}